import React, { useState } from 'react';
import { Clock, Users, CalendarDays, Tag, Hourglass, Play, Copy, Trash2, X, Loader } from 'lucide-react';
import { MappedClass, BookingData } from './InstructorProfile';
import EditClassPage from './EditClass';
import { fetchEnrolledStudents } from './profileAPIs';
import toast from 'react-hot-toast';

interface ClassCardProps {
    cls: MappedClass;
    onCopyMeetLink: (cls: MappedClass) => void;
    onOpenDeleteModal: (classId: string) => void;
    getBookingTypeCounts: (bookings: BookingData[]) => { session: number; monthly: number; yearly: number; };
    onClassUpdated?: () => Promise<void> | void;
}

interface EnrolledStudent {
    _id: string;
    studentId: string;
    studentName: string;
    phone: string;
    pricingType: string;
    bookingDate: string;
    amountPaid: number;
    selectedDates: string[];
    classesAttended: number;
    remainingClasses: number;
    isDemo: boolean;
    status: string;
}

const ClassCard: React.FC<ClassCardProps> = ({ cls, onCopyMeetLink, onOpenDeleteModal, getBookingTypeCounts, onClassUpdated }) => {
    const bookingTypes = getBookingTypeCounts(cls.bookings);
    const [showEnrolledModal, setShowEnrolledModal] = useState(false);
    const [enrolledStudents, setEnrolledStudents] = useState<EnrolledStudent[]>([]);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [activeTab, setActiveTab] = useState<'DEMO' | 'SESSION' | 'MONTHLY' | 'YEARLY'>('SESSION');

    const handleOpenEnrolledModal = async () => {
        setShowEnrolledModal(true);
        setLoadingStudents(true);

        try {
            const response = await fetchEnrolledStudents(cls.id) as any;

            if (response?.success === true && response?.data && Array.isArray(response.data)) {
                console.log("Setting enrolled students:", response.data);
                setEnrolledStudents(response.data);
            } else if (response?.data && Array.isArray(response.data)) {
                // Handle case where API returns data directly without success flag
                console.log("Setting enrolled students (alternative format):", response.data);
                setEnrolledStudents(response.data);
            } else {
                const message = response?.message || "Failed to fetch enrolled students";
                console.error("Error message:", message);
                toast.error(message);
                setEnrolledStudents([]);
            }
        } catch (error) {
            toast.error("Error fetching enrolled students");
            console.error("Catch error:", error);
            setEnrolledStudents([]);
        } finally {
            setLoadingStudents(false);
        }
    };

    const renderSchedule = (scheduleData: MappedClass['scheduleData']) => {
        if (!scheduleData) return 'Not set';
        const activeDays = Object.entries(scheduleData)
            .filter(([, config]) => config.isActive)
            .map(([day]) => day.substring(0, 3)); // e.g., Mon, Tue

        return activeDays.length > 0 ? activeDays.join(', ') : 'No active days';
    };

    const getFilteredStudents = () => {
        if (activeTab === 'DEMO') {
            return enrolledStudents.filter(s => s.isDemo === true);
        }
        return enrolledStudents.filter(s => s.isDemo === false && s.pricingType.toLowerCase() === activeTab.toLowerCase());
    };

    const getStatusDisplay = (student: EnrolledStudent) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day for comparison

        // Check selectedDates if available
        if (student.selectedDates && student.selectedDates.length > 0) {
            const hasUpcomingDate = student.selectedDates.some(dateStr => {
                const selectedDate = new Date(dateStr);
                selectedDate.setHours(0, 0, 0, 0);
                return selectedDate >= today;
            });

            if (!hasUpcomingDate) {
                // All selected dates are in the past
                if (student.isDemo) {
                    return 'Demo Expired';
                } else {
                    return 'Session Expired';
                }
            }
            return 'Confirmed';
        }

        // Fallback to booking date logic if no selectedDates
        const bookingDate = new Date(student.bookingDate);

        if (student.isDemo) {
            // For demos, check if booking date is in the past
            if (bookingDate < today) {
                return 'Demo Expired';
            }
            return 'Confirmed';
        } else {
            // For paid subscriptions, check remaining classes or if date has passed
            if (student.remainingClasses <= 0) {
                return 'Session Expired';
            }
            // Additional check: if pricing type is monthly/yearly, check if it has expired
            if (student.pricingType.toLowerCase() === 'monthly') {
                const expiryDate = new Date(bookingDate);
                expiryDate.setMonth(expiryDate.getMonth() + 1);
                if (expiryDate < today) {
                    return 'Session Expired';
                }
            } else if (student.pricingType.toLowerCase() === 'yearly') {
                const expiryDate = new Date(bookingDate);
                expiryDate.setFullYear(expiryDate.getFullYear() + 1);
                if (expiryDate < today) {
                    return 'Session Expired';
                }
            }
            return 'Confirmed';
        }
    };

    const filteredStudents = getFilteredStudents();

    return (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col">
            {/* Class Thumbnail */}
            {cls.classThumbnail && (
                <div className="relative h-32 w-full overflow-hidden">
                    <img
                        src={cls.classThumbnail}
                        alt={cls.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                    <span className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${cls.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                        {cls.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                </div>
            )}

            <div className="p-4 flex-grow flex flex-col">
                <div className="flex justify-between items-start mb-2">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-1">{cls.title}</h3>
                        <p className="text-purple-600 text-sm font-semibold">{cls.type}</p>
                    </div>
                    {cls.pricingType && (
                        <span className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                            <Tag className="w-4 h-4 mr-1 text-gray-500" />
                            {cls.pricingType}
                        </span>
                    )}
                </div>

                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{cls.description}</p>

                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm text-gray-700 mb-3">
                    <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-blue-500" />
                        {cls.duration}
                    </span>
                    <button
                        onClick={handleOpenEnrolledModal}
                        className="flex items-center cursor-pointer hover:opacity-75 transition-opacity">
                        <Users className="w-4 h-4 mr-2 text-green-500" />
                        {cls.enrolled} Enrolled
                    </button>
                    <span className="flex items-center">
                        <CalendarDays className="w-4 h-4 mr-2 text-red-500" />
                        {renderSchedule(cls.scheduleData)}
                    </span>
                    {cls.demoDuration && (
                        <span className="flex items-center">
                            <Hourglass className="w-4 h-4 mr-2 text-yellow-500" />
                            Demo: {cls.demoDuration}
                        </span>
                    )}
                </div>

                {/* Pricing & Subscriptions */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <div className="grid grid-cols-2 gap-x-4">
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Pricing:</p>
                            <div className="flex flex-col gap-y-1 text-xs">
                                {cls.classPrice.session && <span className="text-blue-600">Session: ₹{cls.classPrice.session}</span>}
                                {cls.classPrice.monthly && <span className="text-green-600">Monthly: ₹{cls.classPrice.monthly}</span>}
                                {cls.classPrice.yearly && <span className="text-purple-600">Yearly: ₹{cls.classPrice.yearly}</span>}
                                {!cls.classPrice.session && !cls.classPrice.monthly && !cls.classPrice.yearly && <span className="text-gray-500">Not set</span>}
                            </div>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-1">Subscriptions:</p>
                            <div className="flex flex-col gap-y-1 text-xs">
                                <span className="text-blue-600">Session: {bookingTypes.session}</span>
                                <span className="text-green-600">Monthly: {bookingTypes.monthly}</span>
                                <span className="text-purple-600">Yearly: {bookingTypes.yearly}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Next Session Display */}
                {cls.nextUpcomingSession && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-sm font-medium text-blue-800">Next: {cls.nextUpcomingSession.title}</p>
                                <p className="text-sm text-blue-600">{cls.nextUpcomingSession.time}</p>
                            </div>
                            <button
                                onClick={() => window.open(cls.nextUpcomingSession!.url, '_blank')}
                                className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm">
                                <Play className="w-4 h-4 mr-1" />
                                Join
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex gap-2 mt-auto"> {/* mt-auto pushes buttons to the bottom */}
                    <button
                        onClick={() => onCopyMeetLink(cls)}
                        className="flex-1 flex items-center justify-center px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all text-sm">
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Link
                    </button>
                    <EditClassPage cls={cls} onUpdated={onClassUpdated} />
                    <button
                        onClick={() => onOpenDeleteModal(cls.id)}
                        className="flex items-center justify-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Enrolled Students Modal */}
            {showEnrolledModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h2 className="text-2xl font-bold text-gray-800">Enrolled Students ({cls.enrolled})</h2>
                            <button
                                onClick={() => setShowEnrolledModal(false)}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                                <X className="w-6 h-6 text-gray-600" />
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 bg-gray-50 px-6">
                            {(['DEMO', 'SESSION', 'MONTHLY', 'YEARLY'] as const).map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-3 font-medium text-sm transition-all ${
                                        activeTab === tab
                                            ? 'border-b-2 border-blue-600 text-blue-600'
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}>
                                    {tab}
                                    <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded-full">
                                        {enrolledStudents.filter(s => 
                                            tab === 'DEMO' ? s.isDemo : s.pricingType.toLowerCase() === tab.toLowerCase() && !s.isDemo
                                        ).length}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingStudents ? (
                                <div className="flex items-center justify-center h-32">
                                    <Loader className="h-6 w-6 text-teal-600 animate-spin mr-2" />
                                    <p className="text-gray-600">Loading enrolled students...</p>
                                </div>
                            ) : filteredStudents && filteredStudents.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b-2 border-gray-300 bg-gray-50">
                                                <th className="text-left px-4 py-3 font-semibold text-gray-700">Student Name</th>
                                                <th className="text-left px-4 py-3 font-semibold text-gray-700">Phone</th>
                                                <th className="text-left px-4 py-3 font-semibold text-gray-700">Booking Date</th>
                                                <th className="text-left px-4 py-3 font-semibold text-gray-700">Pricing Type</th>
                                                <th className="text-left px-4 py-3 font-semibold text-gray-700">Amount Paid</th>
                                                <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                                                <th className="text-left px-4 py-3 font-semibold text-gray-700">Selected Dates</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStudents.map((student) => (
                                                <tr key={student._id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 text-gray-800 font-medium">{student.studentName}</td>
                                                    <td className="px-4 py-3 text-gray-700">{student.phone}</td>
                                                    <td className="px-4 py-3 text-gray-700">{student.bookingDate}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                                            {student.pricingType}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-800 font-semibold">₹{student.amountPaid}</td>
                                                    <td className="px-4 py-3">
                                                        {(() => {
                                                            const displayStatus = getStatusDisplay(student);
                                                            const isExpired = displayStatus.includes('Expired');
                                                            return (
                                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                                    isExpired
                                                                        ? 'bg-red-100 text-red-700'
                                                                        : 'bg-green-100 text-green-700'
                                                                }`}>
                                                                    {displayStatus}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-700 text-xs">
                                                        {student.selectedDates && student.selectedDates.length > 0 ? (
                                                            <div className="flex flex-col gap-1">
                                                                {student.selectedDates.slice(0, 3).map((date, idx) => (
                                                                    <div key={idx} className="bg-gray-100 px-2 py-1 rounded">
                                                                        {new Date(date).toLocaleDateString('en-IN')}
                                                                    </div>
                                                                ))}
                                                                {student.selectedDates.length > 3 && (
                                                                    <div className="text-gray-500 text-xs">
                                                                        +{student.selectedDates.length - 3} more
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400">No dates</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-32">
                                    <p className="text-gray-500 text-lg">No {activeTab.toLowerCase()} enrollments</p>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="border-t border-gray-200 p-4 flex justify-end">
                            <button
                                onClick={() => setShowEnrolledModal(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassCard;
